import React, { useCallback, useMemo } from 'react';
import { Inspector } from 'react-inspector';
import { customTheme } from './inspectorTheme';
import styles from './WidgetPortInspector.module.css';
import { INITIAL_ARRAY_ITEMS, LOAD_MORE_MARKER_PREFIX, parseLoadMoreMarker, renderArrayCount, renderLoadMoreButton } from './helpers';
import { useTranslation } from '@hooks/index';

export type VirtualizedArrayRendererProps = {
  arrayData: unknown[];
  customNodeRenderer: (props: { depth: number, name: string, data: unknown, isNonenumerable: boolean }) => React.ReactElement;
  processNestedArrays: (data: unknown, path?: string) => unknown;
  nestedArrayVisibleCounts: Map<string, number>;
  handleNestedArrayLoadMore: (arrayPath: string, arrayLength: number) => void;
};

export const VirtualizedArrayRenderer = ({
  arrayData,
  customNodeRenderer,
  processNestedArrays,
  nestedArrayVisibleCounts,
  handleNestedArrayLoadMore
}: VirtualizedArrayRendererProps) => {
  const t = useTranslation();
  const wpT = useMemo(() => t.withBaseKey('LogicMapper.WidgetPortDebugPanel'), [t]);
  const arrayLength = arrayData.length;
  const rootPath = ''; // Root array uses empty string as path
  const visibleCount = nestedArrayVisibleCounts.get(rootPath) ?? INITIAL_ARRAY_ITEMS;
  const showLoadMore = visibleCount < arrayLength;

  const handleLoadMore = useCallback(() => {
    handleNestedArrayLoadMore(rootPath, arrayLength);
  }, [arrayLength, handleNestedArrayLoadMore]);

  // Create an array with visible items and a "load more" marker if needed
  const arrayWithLoadMore = useMemo(() => {
    const visibleItems = arrayData.slice(0, visibleCount).map((item, index) =>
      processNestedArrays(item, `[${index}]`)
    );
    return showLoadMore
      ? [...visibleItems, `${LOAD_MORE_MARKER_PREFIX}${rootPath}__${arrayLength - visibleCount}`]
      : visibleItems;
  }, [arrayData, visibleCount, showLoadMore, arrayLength, processNestedArrays]);

  // Custom node renderer that handles the root array load more marker and count
  const arrayNodeRendererWithLoadMore = useCallback(({ depth, name, data, isNonenumerable }: { depth: number, name: string, data: unknown, isNonenumerable: boolean }) => {
    // Handle root level load more marker (path is empty string)
    if (typeof data === 'string') {
      const parsed = parseLoadMoreMarker(data);
      if (parsed && parsed.path === rootPath) {
        return renderLoadMoreButton(parsed.remaining, handleLoadMore, wpT);
      }
    }

    // Handle root level array - show count with visible/total format
    if (depth === 0 && Array.isArray(data)) {
      return renderArrayCount(visibleCount, arrayLength);
    }

    // For nested items, delegate to customNodeRenderer
    return customNodeRenderer({ depth, name, data, isNonenumerable });
  }, [arrayLength, visibleCount, customNodeRenderer, handleLoadMore, wpT]);

  return (
    <div className={styles.VirtualizedArrayContainer}>
      <div className={styles.ArrayInspectorWrapper}>
        <Inspector
          data={arrayWithLoadMore}
          table={false}
          expandLevel={1}
          // @ts-expect-error bad typings
          theme={customTheme}
          nodeRenderer={arrayNodeRendererWithLoadMore}
        />
      </div>
    </div>
  );
};

